import { useSelector } from "react-redux";
import Modal from "../../../../components/atoms/modal";
import LinkedinLogo from "../../../../assets/svg/Linkedin_cardlogo.svg";
import gloLogo from "@lxp-app/assets/svg/glo_logo.svg";
import { cn } from "../../../../utils/cn";
import GetHeroIcon from "../../../../utils/getHeroIcon";
import Tooltip from "../../../../components/atoms/tooltip";
import { RootState } from "../../../../store/store";
import { CertificateItem } from "./certificateCard";

const SHARE_URL_PREFIX = import.meta.env.VITE_CERTIFICATE_SHARE_URL_PREFIX;

// Defaults used when the tenant config omits the corresponding share setting.
const DEFAULT_ORG_ID = "164008";
const DEFAULT_ORG_NAME = "GlobalLogic";
const DEFAULT_INFO_MESSAGE = "You must be logged in to the platform to share.";
const DEFAULT_POST_MESSAGE = `Certified. And just getting started.

GlobalLogic builds products that move industries. It builds people the same way.

Proud to earn my GlobalLogic certification through GLX.

Focused learning. Real growth. Engineering impact.

Ready to put this to work.

#GLX #EngineeringImpact`;

/**
 * Builds the public certificate URL from the env-specific prefix and the
 * certificate's external id. Returns null when sharing isn't possible — i.e.
 * the env prefix is missing, or the certificate has no valid (non-empty string)
 * externalCertificateId — which callers use to disable sharing.
 */
export const getShareCertificateUrl = (
  certificate: CertificateItem
): string | null => {
  if (!SHARE_URL_PREFIX) return null;
  const externalId = certificate?.externalCertificateId;
  if (typeof externalId !== "string" || !externalId.trim()) return null;
  return `${SHARE_URL_PREFIX}${externalId.trim()}`;
};

const buildPostMessage = (body: string, shareUrl: string) =>
  `${body}\n\n${shareUrl}`;

interface ShareCertificateModalProps {
  open: boolean;
  setOpen: (value: boolean) => void;
  certificate: CertificateItem;
  fromAssessmentResult?: boolean;
  labels?: {
    title?: string;
    linkedInPostLabel?: string;
    linkedInProfileLabel?: string;
    gloLabel?: string;
    comingSoon?: string;
    infoMessage?: string;
  };
}

const ShareCertificateModal = ({
  open,
  setOpen,
  certificate,
  fromAssessmentResult = false,
  labels
}: ShareCertificateModalProps) => {
  const shareConfig = useSelector((state: RootState) =>
    fromAssessmentResult
      ? state.AppMetaDataSlice.tenantConfig?.pages?.assessment?.assessmentResult
          ?.shareCertificate
      : state.AppMetaDataSlice.tenantConfig?.pages?.profilePage
          ?.activityDashboard?.shareCertificate
  );

  const orgId = shareConfig?.linkedInOrganizationId || DEFAULT_ORG_ID;
  const orgName = shareConfig?.linkedInOrganizationName || DEFAULT_ORG_NAME;
  const postMessageBody = shareConfig?.postMessage || DEFAULT_POST_MESSAGE;
  const infoMessage =
    shareConfig?.infoMessage || labels?.infoMessage || DEFAULT_INFO_MESSAGE;

  const shareUrl = getShareCertificateUrl(certificate);

  const openInNewTab = (url: string) => {
    window.open(url, "_blank", "noopener,noreferrer");
    setOpen(false);
  };

  const shareAsPost = () => {
    if (!shareUrl) return;
    openInNewTab(
      `https://www.linkedin.com/feed/?shareActive=true&text=${encodeURIComponent(
        buildPostMessage(postMessageBody, shareUrl)
      )}`
    );
  };

  const addToProfile = () => {
    if (!shareUrl) return;
    const params = new URLSearchParams({
      startTask: "CERTIFICATION_NAME",
      name: certificate?.title || `${orgName} Certificate`,
      organizationId: orgId,
      organizationName: orgName,
      certUrl: shareUrl
    });

    const credentialId = certificate?.externalCertificateId?.trim();
    if (credentialId) {
      params.set("certId", credentialId);
    }

    const setYearMonth = (
      value: string | undefined,
      yearKey: string,
      monthKey: string
    ) => {
      if (!value) return;
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return;
      params.set(yearKey, String(date.getFullYear()));
      params.set(monthKey, String(date.getMonth() + 1));
    };

    setYearMonth(certificate?.createdat, "issueYear", "issueMonth");
    setYearMonth(certificate?.expiryDate, "expirationYear", "expirationMonth");

    openInNewTab(`https://www.linkedin.com/profile/add?${params.toString()}`);
  };

  // A channel is listed when its key is present in config; it renders disabled
  // ("Coming soon") when its `enabled` is not true.
  type ChannelKey = "linkedInPost" | "linkedInProfile" | "glo";
  const channelDefs: {
    key: ChannelKey;
    label: string;
    icon: string;
    onClick: () => void;
  }[] = [
    {
      key: "linkedInPost",
      label: labels?.linkedInPostLabel || "Share on LinkedIn as Post",
      icon: LinkedinLogo,
      onClick: shareAsPost
    },
    {
      key: "linkedInProfile",
      label:
        labels?.linkedInProfileLabel ||
        "Add as a Certification to LinkedIn Profile",
      icon: LinkedinLogo,
      onClick: addToProfile
    },
    {
      key: "glo",
      label: labels?.gloLabel || "Share on Glo",
      icon: gloLogo,
      onClick: () => {}
    }
  ];

  const options = channelDefs
    .filter((def) => Boolean(shareConfig?.channels?.[def.key]))
    .map((def) => ({
      id: def.key,
      label: def.label,
      icon: def.icon,
      onClick: def.onClick,
      disabled: shareConfig?.channels?.[def.key]?.enabled !== true
    }));

  return (
    <Modal open={open} setOpen={setOpen} closeBtn dialogueClassName="max-w-md">
      <div className="flex items-center justify-between mb-2">
        <h2 className="font-semibold text-xl text-gray-900 dark:text-gray-50">
          {labels?.title || "Share Achievement"}
        </h2>
        <div className="flex items-center gap-3">
          <Tooltip
            message={infoMessage}
            position="top-7 right-0 w-56 text-left"
          >
            <GetHeroIcon
              name="InformationCircleIcon"
              className="w-5 h-5 text-gray-400 dark:text-zinc-400 cursor-pointer"
              variant="outline"
            />
          </Tooltip>
          <GetHeroIcon
            name="XMarkIcon"
            className="w-5 h-5 flex-shrink-0 font-bold cursor-pointer"
            onClick={() => setOpen(false)}
          />
        </div>
      </div>
      <div className="flex flex-col gap-3 mt-5">
        {options.map((option) => (
          <button
            key={option.id}
            type="button"
            disabled={option.disabled}
            onClick={option.onClick}
            className={cn(
              "flex items-center gap-4 w-full rounded-lg border p-4 text-left transition-colors",
              option.disabled
                ? "cursor-not-allowed opacity-50 border-gray-200 dark:border-zinc-700"
                : "border-gray-200 hover:border-primary hover:bg-[#e2f2ff] dark:border-zinc-700 dark:hover:bg-zinc-700"
            )}
          >
            <img
              onContextMenu={(e) => e.preventDefault()}
              style={{ userSelect: "none" }}
              src={option.icon}
              alt=""
              className="h-8 w-8 flex-shrink-0"
            />
            <span className="text-sm font-medium text-secondary dark:text-zinc-200 whitespace-nowrap">
              {option.label}
            </span>
            {option.disabled && (
              <span className="ml-auto text-xs font-medium text-gray-400 dark:text-zinc-500">
                {labels?.comingSoon || "Coming soon"}
              </span>
            )}
          </button>
        ))}
      </div>
    </Modal>
  );
};

export default ShareCertificateModal;
